import React, { useState } from 'react';
import './projects.css';
import { useNavigate } from "react-router-dom";
import SEO from '../SEO.js';

const TAG_COLORS = {
    Product: 'bg-green-500',
    AI: 'bg-blue-500',
    Research: 'bg-red-500',
    'Creative Tech': 'bg-purple-500',
};

const TAG_ORDER = ['AI', 'Product', 'Research', 'Creative Tech'];

const Projects = () => {

    const navigate = useNavigate()
    const [activeTag, setActiveTag] = useState(null);

    function goToLink(link) {
        window.location.href = link;
    }

    const goToEverydaySounds = () => navigate("/everydaysounds");
    const goToVRCooking = () => navigate("/vrcooking");
    const goToAdsNYTGames = () => navigate("/adsnytgames");
    const goToImageAttribution = () => navigate("/imageattribution");

    const highlighted = [
        {
            id: 'dark-patterns',
            title: 'Dark Patterns & LLM Susceptibility',
            description: 'Studying how AI agents respond to deceptive UI patterns, advised by Dr. Jennifer King at Stanford Institute for Human-Centered AI (HAI).',
            year: '2026',
            tags: ['AI', 'Research'],
            hoverBg: 'hover:bg-orange-200',
            status: 'Publication in-progress',
        },
        {
            id: 'image-attribution',
            title: 'Synthetic/AI Image Attribution',
            description: 'Computer vision system that identifies the source of AI-generated images despite real-world image edits and degradation.',
            year: '2025',
            tags: ['AI', 'Research'],
            hoverBg: 'hover:bg-purple-200',
            onClick: goToImageAttribution,
            arrow: true,
        },
        {
            id: 'nyt-ads',
            title: 'NYT Games - Ad Experience',
            description: 'Led a 12-person team to build the advertising experience for NYT Games',
            year: '2023-25',
            tags: ['Product'],
            hoverBg: 'hover:bg-blue-200',
            onClick: goToAdsNYTGames,
            arrow: true,
        },
    ];

    const more = [
        {
            id: 'productive-struggle',
            title: 'Productive Struggle in LLMs',
            description: 'Tested constrained reasoning transfer across two LLMs — found a narrow window that cuts token usage without accuracy loss.',
            year: '2026',
            tags: ['AI', 'Research'],
            hoverBg: 'hover:bg-rose-200',
            onClick: () => goToLink('/files/productive-struggle-report.pdf'),
            arrow: true,
        },
        {
            id: 'everyday-sounds',
            title: 'Everyday Sounds',
            description: 'Web-based digital audio workstation that transforms everyday sounds into rhythmic compositions.',
            year: '2024',
            tags: ['Creative Tech'],
            hoverBg: 'hover:bg-cyan-200',
            onClick: goToEverydaySounds,
            arrow: true,
            arrowColor: 'text-red-500',
        },
        {
            id: 'vision-reader',
            title: 'NYT - VisionReader',
            description: 'Immersive reading experience for New York Times articles on Apple Vision Pro (VR/AR). Product lead for NYT Maker Week 2023 group project.',
            year: '2023',
            tags: ['Product', 'Creative Tech'],
            hoverBg: 'hover:bg-gray-200',
            onClick: () => goToLink('https://open.nytimes.com/a-week-in-the-life-of-a-team-during-the-new-york-times-annual-hackathon-91b793dc51ab'),
            arrow: true,
        },
        {
            id: 'vr-cooking',
            title: 'VR Cooking Simulator',
            description: 'Virtual reality kitchen environment designed to teach basic cooking skills.',
            year: '2022',
            tags: ['Creative Tech'],
            hoverBg: 'hover:bg-yellow-200',
            onClick: goToVRCooking,
            arrow: true,
        },
        {
            id: 'post-a-day',
            title: 'A Post a Day Keeps the Doctor Away',
            description: <>Human-Computer Interaction research paper analyzing people's willingness to share personal info on online self-diagnosis platforms. <i className="underline">arXiv:2412.10709</i></>,
            year: '2021',
            tags: ['Research'],
            hoverBg: 'hover:bg-fuchsia-200',
            onClick: () => goToLink('https://arxiv.org/abs/2412.10709'),
            arrow: true,
        },
        {
            id: 'twitter-ads',
            title: 'Twitter Ads Audience Sharing',
            description: <>Allows Twitter advertisers to share audiences amongst each other and more effectively reach their audiences. <i> [Twitter PM Internship Project]</i></>,
            year: '2021',
            tags: ['Product'],
            hoverBg: 'hover:bg-blue-200',
            onClick: () => goToLink('https://business.twitter.com/en/help/campaign-setup/campaign-targeting/custom-audiences.html'),
            arrow: true,
        },
        {
            id: 'youtube-live',
            title: 'YouTube Live - Livestream Highlights',
            description: "Identifies high-engagement areas in a user's YouTube livestream and helps them make a shorter highlight video. [Google SWE Internship Project]",
            year: '2020',
            tags: ['Creative Tech'],
            hoverBg: 'hover:bg-red-200',
        },
        {
            id: 'chrome-search',
            title: 'Google Chrome - Search Suggestions',
            description: "Dynamic search suggestions on Google Chrome's homepage via interactive chip tiles. [Google SWE Internship Project]",
            year: '2019',
            tags: ['Creative Tech'],
            hoverBg: 'hover:bg-green-200',
        },
        {
            id: 'lionbot',
            title: 'LionBot',
            description: <>Facebook Messenger chatbot that answers questions about Columbia University life. <i> Served as a developer on a four-person team.</i></>,
            year: '2019',
            tags: ['AI'],
            hoverBg: 'hover:bg-sky-200',
            onClick: () => goToLink('https://github.com/thecolumbialion/lionbot'),
            arrow: true,
        },
    ];

    const filterList = (list) => activeTag ? list.filter(item => item.tags.includes(activeTag)) : list;
    const visibleHighlighted = filterList(highlighted);
    const visibleMore = filterList(more);

    const toggleTag = (tag) => setActiveTag(activeTag === tag ? null : tag);

    const renderProject = (item) => (
        <div
            key={item.id}
            className={`project flex flex-row relative min-h-[5.35rem] md:h-[5.35rem] h-auto pb-6 md:pb-0 border-b-2 border-l-2 pl-4 border-black
            transition transform hover:skew-y-1 hover:border-l-8 ${item.hoverBg} ${item.onClick ? 'cursor-pointer' : ''}`}
            onClick={item.onClick ? (e) => item.onClick() : undefined}
        >
            <div className="title self-center w-2/5 pr-2 font-[350]">{item.title}</div>
            <div className="description self-center w-3/5 pl-3 font-[400] font-raleway">{item.description}</div>
            <div className='absolute bottom-2 pr-2 flex items-center gap-3'>
                <p className='text-[10.5px] md:text-[0.9vw] font-raleway italic'>{item.year}</p>
                <div className='flex items-center gap-1.5'>
                    {item.tags.map(tag => (
                        <span key={tag} className={`text-[8.5px] md:text-[0.7vw] font-raleway lowercase tracking-wide px-1.5 py-0.5 ${TAG_COLORS[tag]} text-white rounded opacity-70`}>{tag}</span>
                    ))}
                </div>
            </div>
            {item.arrow && (
                <p className={`absolute bottom-0 right-0 text-[22px] md:text-[1.6vw] ${item.arrowColor || 'text-red-400'} font-raleway`}>&rarr;</p>
            )}
            {item.status && (
                <p className="absolute bottom-0 right-0 text-[10.5px] md:text-[0.8vw] text-red-500 font-raleway italic">{item.status}</p>
            )}
        </div>
    );

    return (
        <div className='grid'>
                <SEO
                    title="Projects"
                    description="Selected projects by Lord Crawford spanning AI research, product design, and creative technology — including Vessel, Everyday Sounds, VR Cooking, and NYT Games advertising."
                    path="/projects"
                />
                <div className="text-[4vw] ml-[3.5%] mt-[2%] space-y-[-2vw] invisible title-spacer" aria-hidden="true"><div>Lord</div><div>Crawford</div></div>
                <div id="all_project_list" className="mt-[5%] space-y-5 mb-10  w-[90%] h-full justify-self-center">

                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => setActiveTag(null)}
                            className={`text-[11px] md:text-[0.75vw] font-raleway lowercase tracking-wide px-3 py-1 rounded transition ${activeTag === null ? 'bg-stone-800 text-white opacity-100' : 'bg-stone-200 text-stone-600 opacity-70 hover:opacity-100'}`}
                        >
                            All
                        </button>
                        {TAG_ORDER.map(tag => (
                            <button
                                key={tag}
                                onClick={() => toggleTag(tag)}
                                className={`text-[11px] md:text-[0.75vw] font-raleway lowercase tracking-wide px-3 py-1 rounded transition text-white ${TAG_COLORS[tag]} ${activeTag === tag ? 'opacity-100 ring-2 ring-offset-1 ring-stone-800' : 'opacity-40 hover:opacity-70'}`}
                            >
                                {tag}
                            </button>
                        ))}
                    </div>

                    {visibleHighlighted.length > 0 && (
                        <>
                            <div className="text-[17px] md:text-[1.15vw] font-raleway font-medium tracking-wide text-stone-700">
                                Highlighted Work
                            </div>
                            {visibleHighlighted.map(renderProject)}
                        </>
                    )}

                    {visibleMore.length > 0 && (
                        <>
                            <div className="pt-6 text-[17px] md:text-[1.15vw] font-raleway font-medium tracking-wide text-stone-700">
                                More Projects
                            </div>
                            {visibleMore.map(renderProject)}
                        </>
                    )}

                </div>
        </div>
    )
}

export default Projects
